/**
 * Per-stock registries — hand-transcribed from the C# data-plane
 * (tipranks-dotnet-apps) and must stay in sync with it:
 *
 * - STOCK_PAGE_TYPES mirrors TipRanksBffUrlBuilder + TipRanksWebsiteUrlBuilder
 *   (TipRanks.CDN/UrlBuilders/*.cs). `bffSlug` and the Financials fan-out only
 *   exist there — they are NOT derivable from the links registry, which is why
 *   this stays hand-written while the non-stock families in data-purge.ts are
 *   derived. `data-purge.test.ts` guards it against drift by cross-checking
 *   every websitePath against @tipranks/links.
 * - STOCK_ETL_PROCESSES mirrors the per-stock HTTP triggers of the Stock-ETL
 *   function app, and `autoPurges` mirrors the PurgeTickerPageCache call sites
 *   in TipRanks.ETL/Services.
 *
 * The API validates every incoming key against these registries — the client
 * never sends URLs, only registry keys.
 */

export interface StockPageTypeDef {
  key: string;
  label: string;
  /**
   * Legacy tab id (`stock-forecast`, …): the `tab` of website-api
   * `/asset/payload?class=stock`, which the admin payload viewer reads.
   */
  bffSlug: string;
  /** Website paths relative to /stocks/{ticker} ("" = the overview page). */
  websitePaths: readonly string[];
}

export const STOCK_PAGE_TYPES = [
  { key: "overview", label: "Overview", bffSlug: "stock-analysis", websitePaths: [""] },
  { key: "forecast", label: "Forecast", bffSlug: "stock-forecast", websitePaths: ["/forecast"] },
  { key: "ai-analysis", label: "AI Analyst", bffSlug: "stock-aianalyst", websitePaths: ["/stock-analysis"] },
  { key: "dividends", label: "Dividends", bffSlug: "stock-dividends", websitePaths: ["/dividends"] },
  { key: "earnings", label: "Earnings", bffSlug: "stock-earnings-calendar", websitePaths: ["/earnings"] },
  { key: "ownership", label: "Ownership", bffSlug: "stock-ownership", websitePaths: ["/ownership"] },
  { key: "financials", label: "Financials", bffSlug: "stock-financials", websitePaths: ["/financials", "/financials/income-statement", "/financials/balance-sheet", "/financials/cash-flow", "/financials/ratios"] },
  { key: "statistics", label: "Statistics", bffSlug: "stock-statistics", websitePaths: ["/statistics"] },
  { key: "technical-analysis", label: "Technical Analysis", bffSlug: "stock-technical-analysis", websitePaths: ["/technical-analysis"] },
  { key: "historical-prices", label: "Historical Prices", bffSlug: "stock-historical-prices", websitePaths: ["/historical-prices"] },
  { key: "news", label: "News", bffSlug: "stock-news", websitePaths: ["/stock-news"] },
  { key: "chart", label: "Charts", bffSlug: "stock-charts", websitePaths: ["/stock-charts"] },
  { key: "advanced-chart", label: "Advanced Chart", bffSlug: "stock-chart-advanced", websitePaths: ["/advanced-chart"] },
  { key: "drug-pipeline", label: "Drug Pipeline", bffSlug: "stock-drug-pipeline", websitePaths: ["/drug-pipeline"] },
  { key: "similar-stocks", label: "Similar Stocks", bffSlug: "stock-similar", websitePaths: ["/similar-stocks"] },
  { key: "buybacks", label: "Buybacks", bffSlug: "stock-buybacks", websitePaths: ["/buybacks"] },
  { key: "options", label: "Options", bffSlug: "stock-options", websitePaths: ["/options-chain"] },
  { key: "risk-factors", label: "Risk Factors", bffSlug: "stock-risk-factors", websitePaths: ["/risk-factors"] },
  { key: "insider-trading", label: "Insider Trading", bffSlug: "stock-insider-trading", websitePaths: ["/insider-trading"] },
  { key: "hedge-funds", label: "Hedge Funds", bffSlug: "stock-hedge-funds", websitePaths: ["/hedge-funds"] },
  { key: "crowd-wisdom", label: "Crowd Wisdom", bffSlug: "stock-investors", websitePaths: ["/stock-investors"] },
  { key: "politicians", label: "Politicians", bffSlug: "stock-politicians", websitePaths: ["/politicians"] },
  { key: "bloggers", label: "Blogger Opinion", bffSlug: "stock-bloggers", websitePaths: ["/bloggers"] },
  { key: "etf-exposure", label: "ETF Exposure", bffSlug: "stock-exposure", websitePaths: ["/etf-exposure"] },
] as const satisfies readonly StockPageTypeDef[];

export type StockPageTypeKey = (typeof STOCK_PAGE_TYPES)[number]["key"];

export function getStockPageType(key: string): StockPageTypeDef | undefined {
  return STOCK_PAGE_TYPES.find((p) => p.key === key);
}

// ── per-stock ETL process registry ──

/** Which id the ETL trigger's query string takes. */
export type StockEtlIdKind = "stockId" | "stockListingId" | "ticker";

export interface StockEtlProcessDef {
  key: string;
  label: string;
  description: string;
  /** Function route on the Stock-ETL app, called as `?{idKind}={id}`. */
  path: string;
  idKind: StockEtlIdKind;
  /**
   * True when the C# service ends its run with a CDN purge of the affected
   * pages (verified against the PurgeTickerPageCache call sites in
   * TipRanks.ETL/Services). False → offer a manual purge after running.
   */
  autoPurges: boolean;
  /**
   * Page types whose content includes this process's data — i.e. the pages that
   * go stale when it runs. Read forwards for the purge suggestion after a
   * single run; read backwards (getEtlProcessesForPage) for the per-tab update.
   *
   * A page listed here need not be a page this process *updates* — see
   * `inputOnlyFor`.
   */
  pageKeys: readonly StockPageTypeKey[];
  /**
   * Pages where this process is an input another process rebuilds from, rather
   * than the thing that updates them. Always a subset of `pageKeys`: its data
   * still lands on the page, so the purge suggestion is unchanged, but a tab
   * update there runs the rebuilding process instead of this one.
   *
   * The AI Analyst page (/stocks/{ticker}/stock-analysis) is the case:
   * `ai-reports-reset` rebuilds the whole report from the AI factors,
   * financials and earnings transcript, so it is that tab's entire work list.
   */
  inputOnlyFor?: readonly StockPageTypeKey[];
}

export const STOCK_ETL_PROCESSES = [
  // ── stockId-based ──
  { key: "stock-data", label: "Stock Data", description: "Core stock data (profile, general info)", path: "StockDataHttp", idKind: "stockId", autoPurges: false, pageKeys: ["overview"] },
  { key: "stock-listing", label: "Stock Listing", description: "Listing data for the stock", path: "StockListingHttp", idKind: "stockId", autoPurges: false, pageKeys: [] },
  { key: "earnings", label: "Earnings", description: "Earnings data + calendar", path: "EarningsHttp", idKind: "stockId", autoPurges: true, pageKeys: ["earnings", "overview"] },
  { key: "earnings-segments", label: "Earnings Segments", description: "Revenue segments from earnings", path: "EarningsSegmentHttp", idKind: "stockId", autoPurges: true, pageKeys: ["overview"] },
  { key: "earnings-transcript", label: "Earnings Transcript", description: "Earnings call transcript + summary", path: "EarningsTranscriptHttp", idKind: "stockId", autoPurges: true, pageKeys: ["earnings", "overview", "ai-analysis"], inputOnlyFor: ["ai-analysis"] },
  { key: "financials", label: "Financials", description: "Financial statements", path: "FinancialsHttp", idKind: "stockId", autoPurges: true, pageKeys: ["financials", "ai-analysis"], inputOnlyFor: ["ai-analysis"] },
  { key: "kpis", label: "Company KPIs", description: "Company KPI metrics", path: "KpisHttp", idKind: "stockId", autoPurges: false, pageKeys: [] },
  { key: "peers", label: "Stock Peers", description: "Similar stocks / peer group", path: "PeersHttp", idKind: "stockId", autoPurges: true, pageKeys: ["similar-stocks"] },
  { key: "scores", label: "Scores", description: "Stock scores", path: "ScoresHttp", idKind: "stockId", autoPurges: false, pageKeys: ["overview"] },
  { key: "smart-score", label: "Smart Score", description: "Smart Score recompute", path: "SmartScoreHttp", idKind: "stockId", autoPurges: false, pageKeys: ["overview"] },
  { key: "tabs", label: "Tabs", description: "Which website tabs are enabled for the stock", path: "TabsHttp", idKind: "stockId", autoPurges: false, pageKeys: [] },
  { key: "risk-factors", label: "Risk Factors", description: "Risk factor analysis", path: "RiskFactorsHttp", idKind: "stockId", autoPurges: false, pageKeys: ["risk-factors"] },
  // Day-timeframe indicators from tr-technical-indicators-func into stocks_extra.technical
  // (and RSI/STOCH/MACD onto stocks). The C# timer RefreshTechnicalIndicatorsForAllStocks
  // refreshes every stock every 12 h; this is the on-demand run for one stock.
  { key: "technical-indicators", label: "Technical Indicators", description: "Technical indicators (RSI, MACD, moving averages …) for the stock's listings", path: "TechnicalIndicatorsHttp", idKind: "stockId", autoPurges: false, pageKeys: ["technical-analysis"] },
  // The AI factors are a *section* on two pages, not a tab of their own: the
  // Overview (/stocks/{ticker}) and the AI Analyst page
  // (/stocks/{ticker}/stock-analysis). Both go stale when these run, hence both
  // in pageKeys — but on AI Analyst the updater is `ai-reports-reset`, which
  // rebuilds the report from them, so these are input-only there.
  { key: "ai-factors", label: "AI Factors", description: "AI analysis factors — a section on the Overview and AI Analyst pages", path: "AiFactorsHttp", idKind: "stockId", autoPurges: true, pageKeys: ["overview", "ai-analysis"], inputOnlyFor: ["ai-analysis"] },
  { key: "ai-factors-summary", label: "AI Factors Summary", description: "AI factors summary — a section on the Overview and AI Analyst pages", path: "AiFactorsSummaryHttp", idKind: "stockId", autoPurges: false, pageKeys: ["overview", "ai-analysis"], inputOnlyFor: ["ai-analysis"] },
  { key: "bull-bear-summary", label: "Bull/Bear Summary", description: "Bull/bear key points summary", path: "BullBearSummaryHttp", idKind: "stockId", autoPurges: false, pageKeys: ["overview"] },
  { key: "annual-revenue", label: "Annual Revenue", description: "Annual revenue history", path: "StockAnnualRevenueHttp", idKind: "stockId", autoPurges: false, pageKeys: [] },
  { key: "clinical-trials", label: "Clinical Trials", description: "Drug pipeline / clinical trials", path: "ClinicalTrialsHttp", idKind: "stockId", autoPurges: true, pageKeys: ["drug-pipeline"] },
  { key: "news", label: "News", description: "News items for the stock", path: "NewsHttp", idKind: "stockId", autoPurges: true, pageKeys: ["news"] },
  { key: "news-sentiment", label: "News Sentiment", description: "News sentiment for the stock", path: "NewsSentimentByStockHttp", idKind: "stockId", autoPurges: true, pageKeys: ["news"] },
  { key: "news-key-points", label: "News Key Points", description: "News key points extraction", path: "NewsKeyPointsHttp", idKind: "stockId", autoPurges: false, pageKeys: ["news"] },
  { key: "prices-reset", label: "Historical Prices Reset", description: "Rebuild historical prices", path: "PricesResetHttp", idKind: "stockId", autoPurges: false, pageKeys: ["historical-prices"] },
  { key: "market-cap-reset", label: "Market Cap Reset", description: "Rebuild market cap history", path: "MarketCapResetHttp", idKind: "stockId", autoPurges: false, pageKeys: [] },
  { key: "analyst-ratings", label: "Analyst Ratings", description: "Analyst ratings for the stock", path: "AnalystRatingHttp", idKind: "stockId", autoPurges: true, pageKeys: ["forecast"] },
  { key: "blogger-ratings", label: "Blogger Ratings", description: "Blogger ratings for the stock", path: "BloggerRatingHttp", idKind: "stockId", autoPurges: true, pageKeys: ["bloggers"] },
  { key: "hedge-fund-transactions", label: "Hedge Fund Transactions", description: "Hedge fund activity for the stock", path: "HedgeFundTransactionHttp", idKind: "stockId", autoPurges: true, pageKeys: ["hedge-funds"] },
  { key: "insider-transactions", label: "Insider Transactions", description: "Insider activity for the stock", path: "InsiderTransactionHttp", idKind: "stockId", autoPurges: true, pageKeys: ["insider-trading"] },
  { key: "politician-transactions", label: "Politician Transactions", description: "Congressional (STOCK Act) trading for the stock", path: "PoliticianOperationHttp", idKind: "stockId", autoPurges: true, pageKeys: ["politicians"] },
  // ── stockListingId-based ──
  { key: "dividends", label: "Dividends", description: "Dividend data for a listing", path: "DividendsHttp", idKind: "stockListingId", autoPurges: true, pageKeys: ["dividends"] },
  { key: "buybacks", label: "Buybacks", description: "Buyback data for a listing", path: "BuyBacksHttp", idKind: "stockListingId", autoPurges: true, pageKeys: ["buybacks"] },
  { key: "fundamentals", label: "Fundamentals", description: "Fundamentals for a listing", path: "FundamentalsHttp", idKind: "stockListingId", autoPurges: false, pageKeys: [] },
  { key: "options", label: "Options", description: "Options chain data for a listing", path: "OptionsHttp", idKind: "stockListingId", autoPurges: false, pageKeys: ["options"] },
  { key: "real-time-quotes", label: "Real-Time Quotes", description: "Real-time quote refresh for a listing", path: "RealTimeQuotesHttp", idKind: "stockListingId", autoPurges: false, pageKeys: [] },
  // ── ticker-based ──
  // The AI Analyst tab's only process: this rebuilds the whole report, so the
  // things it reads (AI Factors, Financials, Earnings Transcript) are NOT in
  // that tab's work list — re-running them as part of a tab update would be
  // wasted work. It ends with its own page purge.
  { key: "ai-reports-reset", label: "AI Reports Reset", description: "Rebuild the AI Analyst report end to end — the only process that updates that tab", path: "ResetAiReportsByTickerHttp", idKind: "ticker", autoPurges: true, pageKeys: ["ai-analysis"] },
] as const satisfies readonly StockEtlProcessDef[];

export type StockEtlProcessKey = (typeof STOCK_ETL_PROCESSES)[number]["key"];

/**
 * Widened view of the registry. `as const` gives each entry its own literal
 * type, and the ones that don't set the optional `inputOnlyFor` don't have the
 * property at all — so reading it off the union is a type error. Iterate here.
 */
export const STOCK_ETL_PROCESS_LIST: readonly StockEtlProcessDef[] =
  STOCK_ETL_PROCESSES;

export function getStockEtlProcess(key: string): StockEtlProcessDef | undefined {
  return STOCK_ETL_PROCESS_LIST.find((p) => p.key === key);
}

/**
 * Every ETL process that *updates* one page type — the inverse of `pageKeys`
 * minus the processes marked `inputOnlyFor` that page, and the basis of the
 * per-tab update action ("this tab is stale, fix it").
 *
 * Not the same question as "whose data appears on this page" — that's `pageKeys`
 * read forwards, and it's a superset. AI Analyst is where the two differ: four
 * processes' data lands there, but only `ai-reports-reset` rebuilds it.
 *
 * Returns [] for the tabs no per-stock process feeds (Statistics, Charts,
 * Advanced Chart, ETF Exposure): those are computed elsewhere or served
 * straight from the aggregate, so updating them means a purge only.
 * Callers must treat an empty list as valid, not as an error.
 */
export function getEtlProcessesForPage(
  pageKey: string
): readonly StockEtlProcessDef[] {
  return STOCK_ETL_PROCESS_LIST.filter(
    (p) =>
      (p.pageKeys as readonly string[]).includes(pageKey) &&
      !(p.inputOnlyFor as readonly string[] | undefined)?.includes(pageKey)
  );
}
